import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TabsModule } from 'primeng/tabs';
import { finalize, Subscription } from 'rxjs';
import { RilevamentoScambiIntersezioneDoppiService } from '@/core/service/rilevamento-intersezione.service';
import { UtilsService } from '@/core/service/utils/utils.service';

@Component({
    selector: 'app-misure-telaio',
    templateUrl: './misure-telaio.component.html',
    imports: [CommonModule, ReactiveFormsModule, TabsModule],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MisureTelaioComponent implements OnChanges, OnDestroy {
    @Input() form!: FormGroup;
    @Input() normativa: any;
    @Input() prefix = '';
    @Input() sKeys: string[] = [];
    @Input() ruleKeys: string[] = [];
    @Input() extraKeys: string[] = [];
    @Input() deviatoioId!: any;
    @Input() formReady = false;
    @Input() isActive = false;
    @Input() mode!: string;

    ranges: Record<string, { min: number; max: number }> = {};
    lastValues: Record<string, number> = {};
    trendIcons: Record<string, string | null> = {};
    dataReady = false;
    private lastLoaded = false;

    private subscriptions: Subscription[] = [];

    constructor(
        private rilevamentoService: RilevamentoScambiIntersezioneDoppiService,
        private cdr: ChangeDetectorRef,
        private utils: UtilsService
    ) {}

    // -------------------------------
    // LIFECYCLE
    // -------------------------------

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['normativa'] && this.normativa) {
            this.computeRanges();
        }

        if (changes['form'] && this.form) {
            this.setupValueChangeSubscriptions();
        }

        if ((changes['deviatoioId'] || changes['formReady']) && this.formReady && this.deviatoioId && !this.lastLoaded && this.mode === 'edit') {
            this.loadLastRilevamento();
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((s) => s.unsubscribe());
    }

    /** Calcola i range in base alla normativa */
    private computeRanges(): void {
        this.sKeys.forEach((s) => {
            const base = this.normativa[s];
            const min = this.normativa[`min${s.toUpperCase()}`];
            const plus = this.normativa[`plus${s.toUpperCase()}`];

            if (base != null && min != null && plus != null) {
                this.ranges[s] = { min: base - min, max: base + plus };
            }
        });
    }

    /** Carica l'ultimo rilievo */
    private loadLastRilevamento(): void {
        this.dataReady = false;

        this.rilevamentoService
            .getOrFetch(this.deviatoioId, () => this.rilevamentoService.list({ sort: 'id,desc', size: 5 }))
            .pipe(
                finalize(() => {
                    this.dataReady = true;
                    this.lastLoaded = true;
                    this.updateTrendIcons();
                    this.cdr.detectChanges();
                })
            )
            .subscribe({
                next: (data) => {
                    const last = data.find((d) => d.deviatoiRiepilogoGenerale?.id === this.deviatoioId?.id);
                    this.lastValues = {};
                    if (!last) return;

                    const allKeys = this.getAllKeys();
                    const normalizedPrefix = this.prefix.replace(/_$/, ''); // rimuove underscore finale, se presente

                    allKeys.forEach((k) => {
                        // Es. "telaio1_s1" → "telaio1S1"
                        const apiField = `${normalizedPrefix}${k.charAt(0).toUpperCase()}${k.slice(1)}`;
                        this.lastValues[k] = last[apiField] ?? last[k] ?? null;

                        // Se il form ha il controllo corrispondente, setta subito il valore
                        const controlName = `${this.prefix}${k}`;
                        const control = this.form.get(controlName);
                        if (control && this.lastValues[k] != null) {
                            control.setValue(this.lastValues[k], { emitEvent: false });
                        }
                    });
                },
                error: () => {
                    this.lastValues = {};
                }
            });
    }

    /** Crea le subscription a tutti i valueChanges */
    private setupValueChangeSubscriptions(): void {
        // Pulisce eventuali subscription precedenti
        this.subscriptions.forEach((s) => s.unsubscribe());
        this.subscriptions = [];

        const allKeys = this.getAllKeys();
        allKeys.forEach((key) => {
            const control = this.form.get(this.prefix + key);
            if (!control) return;

            const sub = control.valueChanges.subscribe(() => {
                // anche se dataReady non è ancora true, calcola comunque
                const icon = this.calculateTrendIcon(key);
                this.trendIcons[key] = icon;
                this.cdr.markForCheck();
            });

            this.subscriptions.push(sub);
        });
    }

    /** Aggiorna tutte le icone di trend */
    updateTrendIcons(): void {
        this.getAllKeys().forEach((k) => {
            this.trendIcons[k] = this.calculateTrendIcon(k);
        });
    }

    /** Calcola la singola icona */
    private calculateTrendIcon(field: string): string | null {
        const lastValue = this.lastValues[field];
        const control = this.form?.get(this.prefix + field);

        if (!control) return null;

        const current = Number(control.value);
        if (control.value === null || control.value === '' || isNaN(current)) {
            return 'pi pi-equals text-gray-400';
        }

        const prev = Number(lastValue);
        if (isNaN(prev)) return 'pi pi-equals text-gray-400';

        if (current > prev) return 'pi pi-arrow-up text-green-500';
        if (current < prev) return 'pi pi-arrow-down text-red-500';
        return 'pi pi-equals text-gray-400';
    }

    // -------------------------------
    // HELPERS
    // -------------------------------

    private getAllKeys(): string[] {
        return [...this.sKeys, ...this.ruleKeys, ...this.extraKeys];
    }

    getTrendIcon(field: string): string | null {
        return this.trendIcons[field] || null;
    }

    getInputClass(key: string): string {
        const control = this.form?.get(this.prefix + key);
        if (!control) return '';

        // Campo vuoto → nessun colore (Number('') darebbe 0 e colorerebbe la cella vuota)
        const raw = control.value;
        if (raw === null || raw === undefined || raw === '') return '';

        const value = Number(raw);
        const range = this.ranges[key];
        if (!range || isNaN(value)) return '';

        return value >= range.min && value <= range.max ? 'bg-green-100 border-green-400' : 'bg-red-100 border-red-400';
    }

    getRuleClass(field: string): string {
        const controlName = this.getFullControlName(field);
        return this.utils.getRuleClass(controlName, this.form, this.normativa);
    }

    private getFullControlName(field: string): string {
        if (this.form.get(field)) return field; // support both prefixed/unprefixed
        if (!this.prefix) return field;
        const controlName = this.prefix.endsWith('_') ? this.prefix + field : `${this.prefix}_${field}`;
        return controlName.replace(/^_/, '');
    }
}
